/* motion.js — dependency-free motion layer (MOTION_INTENSITY 10).
   Rules for this theme:
   - NO window scroll listeners. Reveals use IntersectionObserver; parallax uses
     CSS scroll-driven animations (see base.css @supports animation-timeline).
   - All motion is gated on prefers-reduced-motion.
   - Re-inits on Shopify theme-editor section load so previews animate correctly. */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---- Scroll reveal (elements + staggered groups) ---- */
  function initReveals(root) {
    var scope = root || document;
    var els = scope.querySelectorAll('[data-reveal], [data-reveal-group]');
    if (!els.length) return;

    if (reduce.matches || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    // A 0.12 threshold is a fraction of the target. Judge.me's reviews box
    // is often taller than a phone screen, and it grows again when the
    // widget hydrates. 12% of that box may never be on screen, so the
    // heading stays opacity:0. Reveal on any intersection of the root.
    // The -8% root margin keeps the fade off the bottom edge.
    function showReveal(el) {
      if (el.classList.contains('is-visible')) return;
      var delay = el.getAttribute('data-reveal-delay');
      if (delay) { el.style.setProperty('--reveal-delay', parseInt(delay, 10) + 'ms'); }
      el.classList.add('is-visible');
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        showReveal(entry.target);
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });

    // Geometry check for the cases IntersectionObserver drops: hydration
    // that resizes a box already peeking into view, and a late widget
    // mount that never crosses a ratio threshold. Same idea as the root
    // margin: on screen, and not only a sliver in the bottom 8%.
    function onScreen(el) {
      var rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;
      var viewH = window.innerHeight || document.documentElement.clientHeight;
      var visible = Math.min(rect.bottom, viewH) - Math.max(rect.top, 0);
      if (visible <= 0) return false;
      if (rect.top < viewH * 0.92) return true;
      return visible >= Math.min(48, rect.height);
    }

    // Only hide elements that are fully below the first screen. Above-the-fold
    // nodes never get reveal-pending, so the stylesheet cannot clip them in
    // before this deferred script runs. Zero-size nodes stay visible too.
    function belowFold(el) {
      var rect = el.getBoundingClientRect();
      if (rect.width <= 0 && rect.height <= 0) return false;
      return rect.top >= (window.innerHeight || document.documentElement.clientHeight);
    }

    var pending = [];
    els.forEach(function (el) {
      if (el.classList.contains('is-visible') || el.classList.contains('reveal-pending')) return;
      if (!belowFold(el)) return;
      el.classList.add('reveal-pending');
      io.observe(el);
      pending.push(el);
    });

    var ro = null;
    var mo = null;
    var sweepQueued = false;
    function queueSweep() {
      if (sweepQueued) return;
      sweepQueued = true;
      requestAnimationFrame(function () {
        sweepQueued = false;
        sweep();
      });
    }
    function sweep() {
      if (!pending.length) return;
      var next = [];
      pending.forEach(function (el) {
        if (!el.isConnected || el.classList.contains('is-visible')) {
          io.unobserve(el);
          return;
        }
        if (onScreen(el)) {
          showReveal(el);
          io.unobserve(el);
          return;
        }
        next.push(el);
      });
      pending = next;
      if (!pending.length) {
        if (ro) ro.disconnect();
        if (mo) mo.disconnect();
      }
    }

    if (pending.length && 'ResizeObserver' in window) {
      ro = new ResizeObserver(queueSweep);
      pending.forEach(function (el) { ro.observe(el); });
    }
    if (pending.length && 'MutationObserver' in window) {
      mo = new MutationObserver(queueSweep);
      pending.forEach(function (el) {
        mo.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      });
    }
    window.addEventListener('load', queueSweep);
    // App widgets (Judge.me) often mount after load. These passes catch a
    // missed observer notification without listening to scroll.
    setTimeout(queueSweep, 1200);
    setTimeout(queueSweep, 4000);
  }

  /* ---- Count-up numbers (stats) ----
     Only a plain number (optional thousands commas and one decimal) animates.
     Compact authored values such as 13B+ and 5.5M are left untouched:
     parseFloat("13B+") is 13 and parseFloat("5.5M") is 5.5, which then
     toFixed() renders as 5.50. A separate suffix setting (70 + %) still counts up. */
  function plainCount(raw) {
    var s = String(raw == null ? '' : raw).trim();
    if (!/^(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?$/.test(s)) return null;
    var numeric = s.replace(/,/g, '');
    var target = parseFloat(numeric);
    if (!isFinite(target)) return null;
    return {
      target: target,
      decimals: (numeric.split('.')[1] || '').length,
      raw: s
    };
  }

  function initCountUp(root) {
    var scope = root || document;
    var nums = scope.querySelectorAll('[data-countup]');
    if (!nums.length) return;

    if (reduce.matches || !('IntersectionObserver' in window)) {
      nums.forEach(function (el) { el.textContent = el.getAttribute('data-countup'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        io.unobserve(el);
        var parsed = plainCount(el.getAttribute('data-countup'));
        if (!parsed) return;
        var duration = 1400, start = null;
        function step(ts) {
          if (start === null) start = ts;
          var p = Math.min((ts - start) / duration, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = p < 1
            ? (parsed.target * eased).toFixed(parsed.decimals)
            : parsed.raw;
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    }, { threshold: 0.4 });

    nums.forEach(function (el) {
      if (plainCount(el.getAttribute('data-countup'))) io.observe(el);
    });
  }

  /* ---- Product carousel (scroll-snap rail: arrows + end-state) ---- */
  function initCarousels(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-carousel]').forEach(function (car) {
      if (car.dataset.carouselBound) return;
      car.dataset.carouselBound = '1';
      var track = car.querySelector('[data-carousel-track]');
      var prev = car.querySelector('[data-carousel-prev]');
      var next = car.querySelector('[data-carousel-next]');
      if (!track) return;
      // Opt-in band rows. Move the buttons into the link's group so they sit
      // in the row header. Other carousels leave the buttons on the rail.
      // Listeners bind after the move, on the same nodes.
      if (car.classList.contains('tb-carousel--header-arrows') && prev && next) {
        var inner = car.closest('.tb-section__inner');
        var link = inner && inner.querySelector('.tb-btn');
        var host = link && link.closest('.tb-group');
        if (host && !host.contains(car)) {
          host.appendChild(prev);
          host.appendChild(next);
        } else {
          car.classList.add('tb-carousel--arrows-rail');
        }
      }
      var behavior = reduce.matches ? 'auto' : 'smooth';

      // Advance one card at a time (card width + gap); scroll-snap lands it cleanly.
      function step() {
        var first = track.firstElementChild;
        if (!first) return track.clientWidth;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      }
      function update() {
        var max = track.scrollWidth - track.clientWidth - 4;
        if (prev) prev.toggleAttribute('disabled', track.scrollLeft <= 4);
        if (next) next.toggleAttribute('disabled', track.scrollLeft >= max);
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: behavior }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: behavior }); });
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    });
  }

  function init(root) { initReveals(root); initCountUp(root); initCarousels(root); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor: re-run when a section is (re)loaded so the preview animates. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
